/** @format */

import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import { EchartReact } from 'src/components'
import { Button, Datepicker, Dialog, Icon, Message, Table, TableColumn } from '@king-design/react'
import dayjs from 'dayjs'
import { useAsync } from 'react-use'
import { previewPrometheusAlertRule } from 'src/api/ApiPrometheusAlarm'
import { numberFormat } from 'src/utils/common'
import { isNumber, cloneDeep } from 'lodash'
import { NoData } from 'src/components'
import { echartTooltipOptions } from 'src/utils/echarts'

interface RulePreviewProps {
  className?: string
  expr: string
  visible: boolean
  onClose: () => void
}

const RulePreview: React.FC<RulePreviewProps> = ({ className, expr, visible, onClose }) => {
  const [time, setTime] = useState<[number, number]>([
    dayjs().subtract(30, 'minute').valueOf(),
    dayjs().valueOf()
  ])
  const chartRef = useRef(null)

  const tableDataRef = useRef([])
  const [legendTable, setLegendTable] = useState([])
  const [selectedLegend, setSelectedLegend] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [tableSort, setTableSort] = useState({})
  const [colorOpt, setColorOpt] = useState([])

  const { value: previewOption } = useAsync(async () => {
    if (visible && time[0] && time[1] && expr) {
      const result = await previewPrometheusAlertRule({
        Query: expr,
        Start: dayjs(time[0]).unix(),
        End: dayjs(time[1]).unix()
      })
      const {
        data: { result: chartData },
        totalCount
      } = result
      if (chartData?.length) {
        // 表格数据
        const tableData = chartData.map(item => {
          const { metric, values } = item
          const name = Object.entries(metric)
            .map(([key, value]) => `${key}="${value}"`)
            .join(',')
          const max = values?.length
            ? Math.max.apply(
                null,
                values.map(item => item[1])
              )
            : '-'
          const min = values?.length
            ? Math.min.apply(
                null,
                values.map(item => item[1])
              )
            : '-'
          const sum = values?.length
            ? values.reduce((acc, item) => {
                return acc + Number(item[1])
              }, 0)
            : '-'
          const avg = values?.length && isNumber(sum) ? numberFormat(sum / values?.length) : '-'
          return {
            name: `{${name}}`,
            max,
            min,
            avg
          }
        })
        setLegendTable(tableData)

        // echart option
        const xDate = chartData[0].values.map(item => dayjs(item[0] * 1000).format('HH:mm:ss'))
        const option = {
          tooltip: {
            ...echartTooltipOptions,
            formatter: params => {
              let result = `<div class="chart-tooltip"><p class="chart-tooltip-title">${params[0].name}</p>`
              params.forEach(item => {
                // 转义 HTML 特殊字符，确保 title 属性正确显示
                const escapedSeriesName = String(item.seriesName || '')
                  .replace(/&/g, '&amp;')
                  .replace(/"/g, '&quot;')
                  .replace(/'/g, '&#39;')
                  .replace(/</g, '&lt;')
                  .replace(/>/g, '&gt;')
                result += `<div class="chart-tooltip-item">
                              <div class="chart-tooltip-item-name" title="${escapedSeriesName}">
                                <div class="chart-tooltip-item-name-marker">${item.marker}</div>
                                <p class="chart-tooltip-item-name-text text-overflow">${
                                  item.seriesName
                                }</p>
                              </div>
                  <div class="chart-tooltip-item-value">${numberFormat(item.value)}</div>
                </div>`
              })
              result += '</div>'
              return result
            }
          },
          legend: {
            show: false,
            selected: {}
          },
          xAxis: {
            type: 'category',
            data: xDate,
            boundaryGap: false
          },
          yAxis: {
            type: 'value',
            axisLabel: {
              showMinLabel: true,
              formatter: value => numberFormat(value)
            },
            axisLine: {
              show: true
            }
          },
          series: chartData.map(item => {
            const { metric, values } = item
            const name = Object.entries(metric)
              .map(([key, value]) => `${key}="${value}"`)
              .join(',')
            return {
              type: 'line',
              name: `{${name}}`,
              data: values?.map(item => item[1]) || [],
              symbolSize: 4,
              showAllSymbol: 'auto',
              smooth: true
            }
          })
        }
        setSelectedLegend(tableData.map(item => item.name))
        setTotalCount(totalCount)
        tableDataRef.current = tableData
        return option
      } else {
        resetTableData()
        return chartData
      }
    } else {
      resetTableData()
      return undefined
    }
  }, [visible, time, expr])

  const resetTableData = () => {
    tableDataRef.current = []
    setLegendTable([])
    setSelectedLegend([])
    setTableSort({})
    setTotalCount(0)
  }

  return (
    <div id="rule-preview">
      <Dialog
        title="PromQL数据预览"
        value={visible}
        width={1096}
        onChangeValue={show => {
          if (!show) {
            onClose()
          }
        }}
        container={() => document.getElementById('rule-preview')}
        slotFooter={<Button onClick={() => onClose()}>关闭</Button>}
      >
        <div className={className}>
          <div className="search_row">
            <div className="search_item">
              <p className="search_item_label">查询时间</p>
              <Datepicker
                width={330}
                clearable={true}
                format="YYYY-MM-DD HH:mm"
                value={time}
                range
                type="datetime"
                max={dayjs()}
                onChangeValue={date => {
                  if (date[0] && date[1]) {
                    const diff = Math.abs(dayjs(date[0]).diff(dayjs(date[1]), 'second'))
                    if (diff > 30 * 60) {
                      Message.error('开始时间与结束时间最大30分钟')
                      setTime([
                        dayjs(date[0]).valueOf(),
                        dayjs(date[0]).add(30, 'minute').valueOf()
                      ])
                    } else {
                      setTime([dayjs(date[0]).valueOf(), dayjs(date[1]).valueOf()])
                    }
                  }
                }}
              />
            </div>
          </div>
          <div>
            <Icon className="k-icon-warning-fill" />
            请注意：共{totalCount}条数据，默认展示20条序列。
          </div>
          <div className="preview_chart">
            {previewOption ? (
              <EchartReact
                ref={chartRef}
                option={previewOption}
                onChartReady={instance => {
                  const colorOpt = instance.getOption().color
                  setColorOpt(Array.isArray(colorOpt) ? colorOpt : [colorOpt])
                }}
              />
            ) : (
              <NoData />
            )}
          </div>
          <div className="legend_table">
            <Table
              checkedKeys={selectedLegend}
              rowKey={data => data.name}
              animation={false}
              data={legendTable}
              sort={tableSort}
              onChangeSort={sort => {
                if (legendTable?.length) {
                  const copyData = cloneDeep(tableDataRef.current)
                  copyData.sort((a, b) => {
                    if (a[sort.key] === '-') {
                      return 1
                    }
                    if (sort.type === 'desc') {
                      return Number(a[sort.key]) > Number(b[sort.key]) ? -1 : 1
                    } else if (sort.type === 'asc') {
                      return Number(a[sort.key]) > Number(b[sort.key]) ? 1 : -1
                    } else {
                      return 0
                    }
                  })
                  setLegendTable(copyData)
                  setTableSort(sort)
                }
              }}
              onChangeCheckedKeys={(keys: string[]) => {
                const chartInstance = chartRef.current?.getEchartsInstance()
                if (chartInstance) {
                  const selected = {}
                  legendTable?.forEach(({ name }) => {
                    selected[name] = keys.includes(name) ? true : false
                  })
                  chartInstance.setOption({
                    legend: { selected }
                  })
                }
                setSelectedLegend(keys)
              }}
              rowClassName={({ name }) => {
                return selectedLegend.includes(name) ? '' : 'table_row_selected'
              }}
            >
              <TableColumn
                key="name"
                title="名称"
                width="608"
                slotTemplate={([{ name }]) => {
                  const colorLen = colorOpt.length
                  const findIndex = tableDataRef?.current?.findIndex(item => item.name === name)
                  return (
                    <div className="name_row">
                      <div
                        style={{
                          width: 12,
                          height: 4,
                          backgroundColor: colorOpt[findIndex % colorLen]
                        }}
                      ></div>
                      <p>{name}</p>
                    </div>
                  )
                }}
              />
              <TableColumn
                key="max"
                title="最大值"
                width="100"
                sortable
                slotTemplate={([{ max }]) => {
                  return <span>{isNumber(max) ? numberFormat(max) : max}</span>
                }}
              />
              <TableColumn
                key="min"
                title="最小值"
                width="100"
                sortable
                slotTemplate={([{ min }]) => {
                  return <span>{isNumber(min) ? numberFormat(min) : min}</span>
                }}
              />
              <TableColumn
                key="avg"
                title="平均值"
                width="100"
                sortable
                slotTemplate={([{ avg }]) => {
                  return <span>{isNumber(avg) ? numberFormat(avg) : avg}</span>
                }}
              />
            </Table>
          </div>
        </div>
      </Dialog>
    </div>
  )
}

export default styled(RulePreview)`
  .search_row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    .search_item {
      display: flex;
      align-items: center;
      margin: 0 16px 16px 0;
      .search_item_label {
        margin: 0px 8px 0 0;
      }
    }
  }
  .preview_chart {
    width: 100%;
    height: 300px;
  }
  .legend_table {
    .name_row {
      display: flex;
      align-items: center;
      p {
        width: 632px;
        margin: 0 0 0 8px;
      }
    }
    .table_row_selected {
      opacity: 0.5;
    }
  }
`
